<template>
  <div
    class="army-container simulator-army"
    :class="[`f-${theme}`, mode === 'edit' ? 'is-edit-mode' : 'is-display-mode']">
    <div
      class="army-line"
      v-for="i in lineCount"
      :key="`line-${i}`">
      <div class="header">
        {{ $t('galaxy.selection.view.line_short', { n: i }) }}
      </div>
      <div
        v-for="j in armyLineSize"
        :key="`cell-${i}-${j}`">
        <!-- Keyboard: an empty slot is a button (opens the ship picker);
             a filled slot takes focus to show its ship card and reveal its
             stack/remove buttons, which are hover-only for the mouse. -->
        <template v-if="mode === 'edit'">
          <template v-if="getTile(i, j) === null">
            <div
              v-press
              :class="{
                'is-hoverable': true,
                'is-active': activeIdx === tileIndex(i, j),
              }"
              class="tile"
              :data-tile-idx="tileIndex(i, j)"
              :aria-label="$t('a11y_portal.sim_empty_slot', slotParams(i, j))"
              :aria-pressed="activeIdx === tileIndex(i, j) ? 'true' : 'false'"
              @click="$emit('pick-tile', tileIndex(i, j))">
            </div>
          </template>
          <template v-else>
            <!-- planned (in-game only): a ship still in a shipyard queue,
                 shown but not editable -->
            <div
              class="tile"
              :class="{
                'is-active': activeIdx === tileIndex(i, j),
                'is-planned': getTile(i, j).planned,
              }"
              :data-tile-idx="tileIndex(i, j)"
              tabindex="0"
              role="group"
              :aria-label="editLabel(i, j)"
              v-tooltip.bottom="editTooltip(i, j)"
              @focus="$emit('hover', getTile(i, j).ship_key, getTile(i, j).level)"
              @mouseenter="$emit('hover', getTile(i, j).ship_key, getTile(i, j).level)">
              <svgicon
                class="tile-icon is-rotated"
                aria-hidden="true"
                :name="`ship/${getTile(i, j).ship_key}`" />
              <div
                v-if="editLevel(i, j)"
                class="tile-level"
                aria-hidden="true">
                {{ editLevel(i, j) }}
              </div>
              <template v-if="!getTile(i, j).planned">
                <div
                  v-if="variantChain(getTile(i, j).ship_key).next"
                  v-press
                  v-tooltip.right="$t('page.fight_simulator.increase_stack')"
                  :aria-label="$t('page.fight_simulator.increase_stack')"
                  class="tile-toast is-hidden top left is-active simulator-arrow"
                  @click.stop="tileAction('bump-up', tileIndex(i, j))">
                  <svgicon name="caret-up" aria-hidden="true" />
                </div>
                <div
                  v-else
                  aria-hidden="true"
                  class="tile-toast is-hidden top left simulator-arrow is-disabled">
                  <svgicon name="caret-up" />
                </div>
                <div
                  v-if="variantChain(getTile(i, j).ship_key).prev"
                  v-press
                  v-tooltip.right="$t('page.fight_simulator.reduce_stack')"
                  :aria-label="$t('page.fight_simulator.reduce_stack')"
                  class="tile-toast is-hidden bottom left is-active simulator-arrow"
                  @click.stop="tileAction('bump-down', tileIndex(i, j))">
                  <svgicon name="caret-down" aria-hidden="true" />
                </div>
                <div
                  v-else
                  aria-hidden="true"
                  class="tile-toast is-hidden bottom left simulator-arrow is-disabled">
                  <svgicon name="caret-down" />
                </div>
                <div
                  v-press
                  v-tooltip.right="$t('page.fight_simulator.remove_ship')"
                  :aria-label="$t('page.fight_simulator.remove_ship')"
                  class="tile-toast is-hidden bottom right is-active"
                  @click.stop="tileAction('clear-tile', tileIndex(i, j))">
                  <svgicon name="close" aria-hidden="true" />
                </div>
              </template>
            </div>
          </template>
        </template>

        <!-- Results: each ship is an image named by its tooltip text. -->
        <template v-else>
          <template v-if="getTile(i, j) === null">
            <div
              class="tile"
              aria-hidden="true"></div>
          </template>
          <template v-else>
            <div
              class="tile"
              :class="displayTileClass(i, j)"
              role="img"
              :aria-label="displayTooltip(i, j).replace(/<br>/g, ', ')"
              v-tooltip.bottom="displayTooltip(i, j)">
              <svgicon
                class="tile-icon is-rotated"
                aria-hidden="true"
                :name="`ship/${getTile(i, j).ship_key}`" />
              <div
                v-if="hasLevel(i, j)"
                class="tile-level">
                {{ levelOf(i, j) }}
              </div>
              <div
                v-if="hasUnits(i, j)"
                class="life-container">
                <template v-if="agg">
                  <div
                    v-if="getAggTile(i, j) && getAggTile(i, j).survived > 0"
                    class="life-content"
                    :style="{ height: aggHullPct(i, j) + '%' }">
                  </div>
                </template>
                <template v-else-if="!diff">
                  <div
                    class="life-content"
                    :style="{ height: tileLifePct(getTile(i, j)) + '%' }">
                  </div>
                </template>
                <template v-else>
                  <div
                    class="life-content is-fadded"
                    :style="{ height: tileLifePct(getTile(i, j)) + '%' }">
                  </div>
                  <div
                    v-if="!diffEmpty(i, j)"
                    class="life-content"
                    :style="{ height: tileLifePct(getDiffTile(i, j)) + '%' }">
                  </div>
                </template>
              </div>
              <div
                v-if="getAggTile(i, j)"
                class="agg-survival">
                <div :style="{ flexGrow: getAggTile(i, j).survived }"></div>
                <div :style="{ flexGrow: aggTotal - getAggTile(i, j).survived }"></div>
              </div>
            </div>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'simulator-army',
  props: {
    // Length 18. Each entry is null (empty slot) or
    // { ship_key: string, level?: number, units?: [{hull}] }.
    // Edit mode passes the lean shape ({ ship_key } only);
    // display mode passes initial-state tiles with units.
    tiles: {
      type: Array,
      required: true,
    },
    // Post-fight final state, same shape as `tiles`. null in edit mode.
    // When present, surviving ships show post-fight health overlaid on a
    // faded pre-fight bar (matches Army.vue's diff overlay).
    diff: {
      type: Array,
      default: null,
    },
    // Multi-run aggregate, length 18: null (empty slot) or
    // { survived: int, hull: null | { p10, p50, p90 } } — survived is
    // battles-out-of-aggTotal, hull quantiles are survivor-only fractions.
    // When present it replaces the diff overlay: the life bar shows the
    // survivor-median hull and a bottom strip shows survived vs destroyed.
    agg: {
      type: Array,
      default: null,
    },
    aggTotal: {
      type: Number,
      default: 0,
    },
    mode: {
      type: String,
      default: 'edit', // 'edit' | 'display'
    },
    theme: {
      type: String,
      default: 'dark-blue',
    },
    activeIdx: {
      type: Number,
      default: -1,
    },
    // Ship data; defaults to the portal dataset. The in-game fleet editor
    // cheat passes the instance's own ship data.
    ships: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      armyLineSize: 3,
    };
  },
  computed: {
    shipsData() {
      return this.ships || this.$store.state.portal.data.ship || [];
    },
    lineCount() {
      return this.tiles.length / this.armyLineSize;
    },
  },
  methods: {
    tileIndex(line, nth) {
      return ((line - 1) * this.armyLineSize) + (nth - 1);
    },
    slotParams(line, nth) {
      return { line, slot: nth };
    },
    // Remove / stack buttons can vanish with the change they make (the slot
    // empties, or the arrow greys out at the end of the chain). Put focus
    // back on the slot so a keyboard user isn't dropped to <body>.
    tileAction(event, idx) {
      this.$emit(event, idx);
      this.$nextTick(() => {
        const active = document.activeElement;
        if (active && active !== document.body && this.$el.contains(active)) return;
        const tile = this.$el.querySelector(`[data-tile-idx="${idx}"]`);
        if (tile) tile.focus();
      });
    },
    // Screen-reader name of a filled edit tile: slot, ship × count, level.
    editLabel(line, nth) {
      const parts = [`${this.$t('a11y_portal.sim_slot', this.slotParams(line, nth))}: ${this.editTooltip(line, nth)}`];
      const level = this.editLevel(line, nth);
      if (level) parts.push(this.$t('a11y_portal.level', { level }));
      return parts.join(', ');
    },
    getTile(line, nth) {
      return this.tiles[this.tileIndex(line, nth)];
    },
    getDiffTile(line, nth) {
      return this.diff ? this.diff[this.tileIndex(line, nth)] : null;
    },
    getAggTile(line, nth) {
      return this.agg ? this.agg[this.tileIndex(line, nth)] : null;
    },
    // Three discrete reliability bands (continuous opacity is unreadable):
    // full brightness = comes home in ≥95% of battles, dimmed = at risk,
    // heavily dimmed = lost in ≥95% of battles.
    displayTileClass(line, nth) {
      if (this.agg) {
        const a = this.getAggTile(line, nth);
        if (!a || this.aggTotal <= 0) return {};
        const lost = a.survived <= this.aggTotal * 0.05;
        return {
          'is-agg-lost': lost,
          'is-agg-risky': !lost && a.survived < this.aggTotal * 0.95,
        };
      }
      return { 'is-destroyed': this.diff && this.diffEmpty(line, nth) };
    },
    aggHullPct(line, nth) {
      const a = this.getAggTile(line, nth);
      if (!a || !a.hull) return 0;
      return Math.max(0, Math.min(100, a.hull.p50 * 100));
    },
    // Percent rounded to the nearest 5: at n=100 the quantiles carry real
    // sampling noise, so finer precision would be false.
    pct5(frac) {
      return Math.max(0, Math.min(100, Math.round((frac * 100) / 5) * 5));
    },
    aggTooltip(name, a) {
      const n = this.aggTotal;
      const destroyed = n - a.survived;
      const lines = [name];
      if (destroyed === 0) {
        lines.push(this.$t('page.fight_simulator.agg_survived_all', { n }));
      } else if (a.survived === 0) {
        lines.push(this.$t('page.fight_simulator.agg_destroyed_all', { n }));
      } else {
        lines.push(this.$t('page.fight_simulator.agg_destroyed_count', { count: destroyed, n }));
      }
      if (a.survived > 0 && a.hull) {
        const low = this.pct5(a.hull.p10);
        const high = this.pct5(a.hull.p90);
        lines.push(low === high
          ? this.$t('page.fight_simulator.agg_hull_single', { pct: high })
          : this.$t('page.fight_simulator.agg_hull_range', { low, high }));
      }
      return lines.join('<br>');
    },
    diffEmpty(line, nth) {
      const d = this.getDiffTile(line, nth);
      return !d || d.ship_key == null;
    },
    hasLevel(line, nth) {
      const t = this.diff ? this.getDiffTile(line, nth) : this.getTile(line, nth);
      return t && typeof t.level === 'number';
    },
    levelOf(line, nth) {
      const t = this.diff ? this.getDiffTile(line, nth) : this.getTile(line, nth);
      if (!t || typeof t.level !== 'number') return '';
      return t.level + 1;
    },
    // Edit-mode level badge: the level a placed ship will fight at (1-indexed,
    // hidden at base level 0). Display mode uses levelOf() with the diff state.
    editLevel(line, nth) {
      const t = this.getTile(line, nth);
      return t && typeof t.level === 'number' && t.level > 0 ? t.level + 1 : '';
    },
    hasUnits(line, nth) {
      const t = this.getTile(line, nth);
      return t && Array.isArray(t.units) && t.units.length > 0;
    },
    tileLifePct(tile) {
      if (!tile || !Array.isArray(tile.units)) return 0;
      const shipData = this.shipsData.find((s) => s.key === tile.ship_key);
      if (!shipData) return 0;
      const max = shipData.unit_hull * shipData.unit_count;
      const cur = tile.units.reduce((acc, u) => acc + u.hull, 0);
      return Math.max(0, Math.min(100, (cur / max) * 100));
    },
    displayTooltip(line, nth) {
      const t = this.getTile(line, nth);
      if (!t) return '';
      const name = this.$t(`data.ship.${t.ship_key}.name`);
      if (this.agg) {
        const a = this.getAggTile(line, nth);
        return a ? this.aggTooltip(name, a) : name;
      }
      if (this.diff && this.diffEmpty(line, nth)) {
        return `${name} — ${this.$t('page.fight_simulator.tile_destroyed')}`;
      }
      return name;
    },
    editTooltip(line, nth) {
      const t = this.getTile(line, nth);
      if (!t) return '';
      const ship = this.shipsData.find((s) => s.key === t.ship_key);
      const name = this.$t(`data.ship.${t.ship_key}.name`);
      const count = ship ? ship.unit_count : '?';
      return t.planned
        ? `${name} × ${count} — ${this.$t('panel.empire.cheats_fleet_planned')}`
        : `${name} × ${count}`;
    },
    // Walk the model's variants sorted by unit_count ascending so we can
    // step in either direction. We deliberately don't use the merge_to
    // chain — there's no inverse link, but unit_count is monotonic per
    // model so sorting gives both directions for free.
    variantChain(shipKey) {
      const ship = this.shipsData.find((s) => s.key === shipKey);
      if (!ship) return { prev: null, next: null };
      const variants = this.shipsData
        .filter((s) => s.model === ship.model)
        .sort((a, b) => a.unit_count - b.unit_count);
      const idx = variants.findIndex((s) => s.key === shipKey);
      return {
        prev: idx > 0 ? variants[idx - 1].key : null,
        next: idx < variants.length - 1 ? variants[idx + 1].key : null,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.simulator-arrow {
  cursor: pointer;

  &.is-disabled {
    opacity: 0.3;
    cursor: default;
  }
}

// The stack/remove buttons show on hover; keyboard focus on the tile (or
// on one of them) shows them too, so they can be reached with Tab.
.simulator-army .tile:focus-within > .tile-toast.is-hidden {
  display: block;
}

// Multi-run reliability bands (see displayTileClass). Discrete on purpose.
// is-planned: an in-game ship still in a shipyard queue (fleet editor).
.simulator-army .tile {
  &.is-planned {
    opacity: .4;
  }

  &.is-agg-risky {
    opacity: .7;
  }

  &.is-agg-lost {
    opacity: .3;
  }
}

// Survived-vs-destroyed strip along the tile's bottom edge: the green share
// is the battles (out of aggTotal) the ship came home from, red the battles
// it was lost. Semantic green/red, not faction colours — it reads the same
// for both fleets.
.agg-survival {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  display: flex;

  > div {
    flex-basis: 0;
  }

  > div:first-child {
    background: #3fa66a;
  }

  > div:last-child {
    background: #c8404f;
  }
}

// Theme-coloured frame around each side's fleet so the two players are
// instantly distinguishable. Colours hardcoded to match the faction palette
// in shared/variables.scss because the $themes-list map isn't visible from
// inside this scoped block.
.simulator-army {
  display: inline-block;
  padding: 4px;
  border-radius: 4px;
  border: solid 2px transparent;

  &.f-red {
    border-color: #bc2433;
    box-shadow: 0 0 8px rgba(188, 36, 51, 0.4);
  }

  &.f-dark-blue {
    border-color: #3f66df;
    box-shadow: 0 0 8px rgba(63, 102, 223, 0.4);
  }
}

// Fluid edit grid: the six columns split whatever width the aside offers
// (it scales between 240px and 342px on desktop, and is full-width on
// mobile), so the 6th column can never wrap to a second row. The 340px cap
// keeps tiles at ~48px — bigger than the in-game 40px, which is the point
// of this read-heavy view — instead of ballooning on a full-width mobile
// aside. Display mode (results) keeps the default fixed 40px tiles so the
// diff overlay stays compact alongside the per-round log.
.simulator-army.is-edit-mode {
  display: flex;
  width: 100%;
  max-width: 340px;

  .army-line {
    flex: 1 1 0;
    min-width: 0;
    margin-right: 0;

    .tile {
      margin: 4px 2px;
      width: auto;
      // Fallback for engines without aspect-ratio (pre-Chrome-88 embeds):
      // fixed height gives near-square tiles at full aside width.
      height: 44px;

      @supports (aspect-ratio: 1) {
        height: auto;
        aspect-ratio: 1;
      }

      .tile-icon {
        margin: 2px;
        width: calc(100% - 4px);
        height: calc(100% - 4px);
      }
    }
  }
}
</style>
